import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useStoreConfig } from '@/contexts/StoreConfigContext';
import { Helmet } from 'react-helmet-async';
import Header from '@/components/storefront/Header';
import Footer from '@/components/storefront/Footer';
import ProductCard from '@/components/storefront/ProductCard';
import { SlidersHorizontal, ChevronDown, X } from 'lucide-react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [mobileFilters, setMobileFilters] = useState(false);
  const [inStockOnly, setInStockOnly] = useState(false);
  const { formatPrice } = useCurrency();
  const { config } = useStoreConfig();
  const storeName = config?.store_name || 'Florashop';
  const [searchParams, setSearchParams] = useSearchParams();

  const category = searchParams.get('category') || '';
  const sort = searchParams.get('sort') || '-created_at';
  const search = searchParams.get('search') || searchParams.get('q') || '';

  useEffect(() => {
    api.get('/products/categories').then(r => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 12, sort });
    if (category) params.set('category', category);
    if (search) params.set('search', search);
    api.get(`/products?${params}`).then(r => {
      let prods = r.data.products || [];
      if (inStockOnly) prods = prods.filter(p => p.stock_quantity > 0);
      setProducts(prods);
      setTotal(r.data.total);
      setPages(r.data.pages);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [page, category, sort, search, inStockOnly]);

  const setFilter = (key, val) => {
    const p = new URLSearchParams(searchParams);
    if (val) p.set(key, val); else p.delete(key);
    setSearchParams(p);
    setPage(1);
  };

  const pageTitle = category || (search ? `Search: "${search}"` : 'All Products');

  const FilterSidebar = ({ mobile }) => (
    <div className={`${mobile ? 'p-6' : 'pr-8'}`}>
      {mobile && (
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-sans text-base font-bold" style={{ color: '#000' }}>Filters</h3>
          <button onClick={() => setMobileFilters(false)}><X size={20} /></button>
        </div>
      )}
      {!mobile && (
        <div className="flex items-center gap-2 mb-6">
          <SlidersHorizontal size={16} style={{ color: '#000' }} />
          <h3 className="font-sans text-sm font-bold uppercase tracking-wider" style={{ color: '#000' }}>Filters</h3>
        </div>
      )}

      {/* In Stock */}
      <div className="mb-6 pb-6 border-b" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
        <div className="flex items-center justify-between">
          <span className="font-sans text-sm" style={{ color: '#333' }}>In stock only</span>
          <Switch checked={inStockOnly} onCheckedChange={setInStockOnly} data-testid="filter-in-stock" />
        </div>
      </div>

      {/* Categories */}
      <div className="mb-6">
        <p className="font-sans text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#000' }}>Category</p>
        <div className="space-y-2">
          <button onClick={() => setFilter('category', '')}
            className={`block w-full text-left font-sans text-sm py-1 transition-colors ${!category ? 'font-semibold' : ''}`}
            style={{ color: !category ? '#000' : '#666' }}>
            All Products
          </button>
          {categories.map(c => (
            <button key={c} onClick={() => setFilter('category', c)}
              className={`block w-full text-left font-sans text-sm py-1 transition-colors ${category === c ? 'font-semibold' : ''}`}
              style={{ color: category === c ? '#000' : '#666' }}>
              {c}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="shop-page" className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      <Helmet><title>{`${category || 'Shop All'} — ${storeName}`}</title></Helmet>
      <Header />

      {/* Page Title */}
      <div className="py-10 text-center border-b" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
        <h1 className="font-serif text-3xl md:text-4xl tracking-tight uppercase" style={{ color: '#000' }}>
          {pageTitle}
        </h1>
        <p className="font-sans text-sm mt-2" style={{ color: '#888' }}>{total} products</p>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <div className="hidden lg:block w-56 flex-shrink-0">
            <FilterSidebar />
          </div>

          {/* Products */}
          <div className="flex-1">
            {/* Top bar */}
            <div className="flex items-center justify-between mb-6">
              <button onClick={() => setMobileFilters(true)}
                className="lg:hidden flex items-center gap-2 font-sans text-sm border px-3 py-2"
                style={{ borderColor: 'rgba(0,0,0,0.1)', color: '#333' }}>
                <SlidersHorizontal size={15} /> Filters
              </button>
              <div className="ml-auto">
                <Select value={sort} onValueChange={v => setFilter('sort', v)}>
                  <SelectTrigger data-testid="sort-select" className="w-[180px] font-sans text-xs rounded-none h-9 border"
                    style={{ borderColor: 'rgba(0,0,0,0.1)' }}>
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="-created_at">Newest</SelectItem>
                    <SelectItem value="price">Price: Low to High</SelectItem>
                    <SelectItem value="-price">Price: High to Low</SelectItem>
                    <SelectItem value="title">Alphabetical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-square rounded mb-3" style={{ backgroundColor: 'rgba(0,0,0,0.04)' }} />
                    <div className="h-4 w-2/3 rounded mb-2" style={{ backgroundColor: 'rgba(0,0,0,0.04)' }} />
                    <div className="h-3 w-1/3 rounded" style={{ backgroundColor: 'rgba(0,0,0,0.04)' }} />
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="py-20 text-center">
                <p className="font-sans text-base" style={{ color: '#999' }}>No products found</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {products.map(p => <ProductCard key={p.handle} product={p} formatPrice={formatPrice} />)}
              </div>
            )}

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                {Array.from({ length: pages }).map((_, i) => (
                  <button key={i} onClick={() => setPage(i + 1)}
                    className="w-10 h-10 font-sans text-sm transition-colors"
                    style={{
                      backgroundColor: page === i + 1 ? '#000' : 'transparent',
                      color: page === i + 1 ? '#fff' : '#333',
                      border: page === i + 1 ? 'none' : '1px solid rgba(0,0,0,0.1)',
                    }}>
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      {mobileFilters && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/20" onClick={() => setMobileFilters(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-xl overflow-y-auto">
            <FilterSidebar mobile />
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
